import { isEmpty } from "lodash";
import qs from "qs";
import { z } from "zod";

import {
  BidEntitiesInput_Entities as BidEntity,
  SearchLeadsRequest,
  Workflow_Transitions,
} from "lib/generated/app-service-gql/graphql";
import { routes } from "lib/routes";
import { BidEntityType, LeadSortBy, LeadSortOrder } from "lib/types/graphQLEnums";
import {
  AssigneeFilterSchema,
  ColumnSetting,
  CountryRegionSchema,
  DateFilterSchema,
  nonEmptyUserFilter,
  ValueFilterSchema,
} from "lib/types/models";
import { LeadDetails } from "../../../lib/hooks/api/leads/useLead";
import { LeadWorkflowTransitionGroup } from "../../../lib/hooks/api/leads/useLeadWorkflow";

export type LeadColumns =
  | "name"
  | "buyerName"
  | "stageName"
  | "assignedTo"
  | "createdBy"
  | "value"
  | "framework"
  | "createdAt"
  | "updatedAt"
  | "suppliers"
  | "expiryDate";

export const LeadFiltersSchema = z.object({
  searchText: z.string().optional(),
  value: ValueFilterSchema.optional(),
  assignee: AssigneeFilterSchema.optional(),
  createdBy: AssigneeFilterSchema.optional(),
  stageIds: z.array(z.uuid()).optional(),
  lastUpdatedDate: DateFilterSchema.optional(),
  expiryDate: DateFilterSchema.optional(),
  createdDate: DateFilterSchema.optional(),
  buyers: z.array(z.uuid()).optional(),
  customBuyers: z.array(z.uuid()).optional(),
  buyerLists: z.array(z.uuid()).optional(),
  buyerCategories: z.array(z.uuid()).optional(),
  buyerCountryRegions: CountryRegionSchema.optional(),
  supplierGuids: z.array(z.uuid()).optional(),
  supplierSme: z.string().optional(),
  teamIds: z.array(z.uuid()).optional(),
  order: z.enum(LeadSortOrder).nullable(),
  orderBy: z.enum(LeadSortBy),
});
export type LeadFilters = z.infer<typeof LeadFiltersSchema>;

// Define empty values explicitly as otherwise nested filter form fields fail to be cleared properly
export const EMPTY_LEAD_FILTERS: LeadFilters = {
  searchText: "",
  assignee: { ids: [], includeNulls: undefined },
  createdBy: { ids: [], includeNulls: undefined },
  stageIds: [],
  lastUpdatedDate: { filter: undefined, hideNulls: undefined },
  expiryDate: { filter: undefined, hideNulls: undefined },
  createdDate: { filter: undefined, hideNulls: undefined },
  value: { from: undefined, to: undefined, hideNulls: undefined },
  buyers: [],
  customBuyers: [],
  buyerLists: [],
  buyerCategories: [],
  buyerCountryRegions: { countries: [], regions: [], selected: [] },
  orderBy: LeadSortBy.UpdatedAt,
  order: LeadSortOrder.Desc,
  supplierGuids: [],
  supplierSme: undefined,
  teamIds: [],
};

export const DefaultLeadWorkflowTransitionGroups = [
  LeadWorkflowTransitionGroup.InProgress,
  LeadWorkflowTransitionGroup.Converted,
];

export function getDefaultLeadStageIds(transitions: Workflow_Transitions[]) {
  return (
    transitions
      .filter((transition) =>
        DefaultLeadWorkflowTransitionGroups.map(String).includes(transition.group),
      )
      .map((transition) => transition.stage.id) ?? []
  );
}

export function convertLeadFiltersToSearchRequest(filters: LeadFilters): SearchLeadsRequest {
  let buyerFilters: SearchLeadsRequest["buyerFilters"];
  if (
    filters.buyers?.length ||
    filters.customBuyers?.length ||
    filters.buyerLists?.length ||
    filters.buyerCategories?.length ||
    filters.buyerCountryRegions?.countries?.length ||
    filters.buyerCountryRegions?.regions?.length
  ) {
    buyerFilters = {
      buyers: filters.buyers,
      customBuyers: filters.customBuyers,
      buyerLists: filters.buyerLists,
      buyerCategories: filters.buyerCategories,
      buyerCountryCodes: filters.buyerCountryRegions?.countries,
      buyerRegions: filters.buyerCountryRegions?.regions,
    };
  }

  let supplierFilters: SearchLeadsRequest["supplierFilters"];
  if (filters.supplierGuids?.length || filters.supplierSme) {
    supplierFilters = {
      ids: filters.supplierGuids?.filter(
        (id) => !["SIGNALS:ALL_COMPETITORS", "SIGNALS:ALL_PARTNERS"].includes(id),
      ),
      isSME: filters.supplierSme ? filters.supplierSme.toLowerCase() === "true" : undefined,
    };
  }

  return {
    searchText: filters.searchText?.length ? filters.searchText : undefined,
    assignee: nonEmptyUserFilter(filters.assignee),
    createdBy: nonEmptyUserFilter(filters.createdBy),
    stageIds: filters.stageIds,
    lastUpdatedDate: isEmpty(filters.lastUpdatedDate) ? undefined : filters.lastUpdatedDate,
    expiryDate: isEmpty(filters.expiryDate) ? undefined : filters.expiryDate,
    createdDate: isEmpty(filters.createdDate) ? undefined : filters.createdDate,
    value: isEmpty(filters.value) ? undefined : filters.value,
    buyerFilters,
    supplierFilters,
    teamIds: filters.teamIds,
    order: filters.order,
    orderBy: filters.orderBy,
  };
}

export const DEFAULT_PAGINATION: { pageSize: number; page: number } = {
  page: 1,
  pageSize: 10,
};

export function generateLeadsSearchUrl(filters: LeadFilters): string {
  const params = qs.stringify(
    { leadFilters: filters },
    { arrayFormat: "brackets", addQueryPrefix: true },
  );
  return `${routes.leads}${params}`;
}

const keysToKeep: (keyof LeadDetails)[] = ["id", "name", "description", "value"];

export const filterObject = (obj: Record<string, unknown>, keys: string[]) => {
  return Object.keys(obj)
    .filter((key) => keys.includes(key))
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
};

export const filterLeadDefaultValues = (lead: LeadDetails) => {
  const filteredPayload = filterObject(lead, keysToKeep);
  return {
    ...filteredPayload,
    assignedToId: lead.assignedTo?.guid ?? null,
    stageId: lead.stage.id,
  };
};

export const ALL_AVAILABLE_LEAD_COLUMNS: ColumnSetting<LeadColumns>[] = [
  { title: "Lead name", field: "name", disabled: true },
  { title: "Buyer", field: "buyerName" },
  { title: "Stage", field: "stageName" },
  { title: "Assigned to", field: "assignedTo" },
  { title: "Created by", field: "createdBy" },
  { title: "Value", field: "value" },
  { title: "Created", field: "createdAt" },
  { title: "Last updated", field: "updatedAt" },
  { title: "Expiry date", field: "expiryDate" },
  { title: "Relevant suppliers", field: "suppliers" },
  { title: "Framework", field: "framework" },
];

// Columns shown by default. The "Created by" column is opt-in and excluded.
export const DEFAULT_VISIBLE_LEAD_COLUMNS: ColumnSetting<LeadColumns>[] =
  ALL_AVAILABLE_LEAD_COLUMNS.filter((c) => c.field !== "createdBy");

const columnCopies: Record<LeadColumns, string> = ALL_AVAILABLE_LEAD_COLUMNS.reduce(
  (acc, { title, field }) => {
    return { ...acc, [field]: title };
  },
  {} as Record<LeadColumns, string>,
);

export const getLeadsColumnName = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as LeadColumns];
};

export function mapSortOrderToEnum(order: "ASC" | "DESC" | null | undefined): LeadSortOrder | null {
  if (!order) return null;
  return order === "ASC" ? LeadSortOrder.Asc : LeadSortOrder.Desc;
}

export function mapSortFieldToEnum(field: string): LeadSortBy {
  switch (field) {
    case "name":
      return LeadSortBy.Name;
    case "value":
      return LeadSortBy.Value;
    case "createdAt":
      return LeadSortBy.CreatedAt;
    case "updatedAt":
      return LeadSortBy.UpdatedAt;
    case "buyerName":
      return LeadSortBy.BuyerName;
    case "assignedTo":
      return LeadSortBy.AssignedTo;
    case "createdBy":
      return LeadSortBy.CreatedBy;
    case "stageName":
      return LeadSortBy.StageName;
    case "expiryDate":
      return LeadSortBy.ExpiryDate;
    default:
      return LeadSortBy.CreatedAt;
  }
}

/**
 * Converts a lead's entities to the BidEntitiesInput format, to pre-populate a bid created from
 * the lead. A bid has one framework, so only the lead's first is carried over.
 */
export function getLeadEntitiesForBid(lead: LeadDetails): BidEntity[] {
  const entities: BidEntity[] = [];

  // Add buyers as organisations
  for (const buyer of lead.buyers) {
    entities.push({
      entityId: buyer.id,
      entityType: BidEntityType.Buyer,
    });
  }

  // Add suppliers as organisations
  for (const supplier of lead.suppliers) {
    entities.push({
      entityId: supplier.id,
      entityType: BidEntityType.Supplier,
    });
  }

  // Add custom buyers as custom_organisations
  for (const customBuyer of lead.customBuyers) {
    entities.push({
      entityId: customBuyer.id,
      entityType: BidEntityType.CustomOrganisation,
    });
  }

  // Add contacts
  for (const contact of lead.contacts) {
    entities.push({
      entityId: contact.id,
      entityType: BidEntityType.Contact,
    });
  }

  // Add documents
  for (const document of lead.documents) {
    entities.push({
      entityId: document.id,
      entityType: BidEntityType.Document,
    });
  }

  // A bid has one framework, so it takes the lead's first; createBid refuses more than one
  const [framework] = lead.frameworks;
  if (framework) {
    entities.push({
      entityId: framework.id,
      entityType: BidEntityType.Framework,
    });
  }

  return entities;
}
